import { useCallback, useEffect, useState } from "react";
import apiClient from "../api-services/ApiClient";

export type AdminNotification = {
  id: number;
  type: "elite_application" | "payment_mismatch" | "subscription_grant" | "price_change";
  title: string;
  body: string | null;
  link: string | null;
  created_at: string;
  read: boolean;
};

const POLL_MS = 60_000;
const CHANGED = "admin-notifications-changed";

/** Call after changing read state elsewhere (e.g. the Notifications page) so the bell updates now */
export const announceNotificationsChanged = () => window.dispatchEvent(new Event(CHANGED));

/**
 * Bell data: unread count (refreshed every minute while the tab is visible) and
 * the latest notifications (loaded when the bell opens).
 */
const useAdminNotifications = () => {
  const [unread, setUnread] = useState(0);
  const [latest, setLatest] = useState<AdminNotification[]>([]);
  const [loading, setLoading] = useState(false);

  const refreshCount = useCallback(async () => {
    try {
      const res: any = await apiClient.get("notifications/unread-count");
      setUnread(Number(res?.data?.unread_count) || 0);
    } catch {
      /* the bell just keeps its last count */
    }
  }, []);

  const loadLatest = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("notifications", { params: { per_page: 8 } });
      setLatest(res?.data?.data ?? []);
      setUnread(Number(res?.data?.unread_count) || 0);
    } catch {
      /* keep what we had */
    } finally {
      setLoading(false);
    }
  }, []);

  const markRead = useCallback(async (id: number) => {
    setLatest((list) => list.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try {
      const res: any = await apiClient.post(`notifications/${id}/read`);
      setUnread(Number(res?.data?.unread_count) || 0);
    } catch {
      /* harmless: it stays unread on the server */
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await apiClient.post("notifications/read-all");
      setLatest((list) => list.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch {
      /* keep state */
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const tick = () => {
      if (document.visibilityState === "visible") refreshCount();
    };
    const id = window.setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener(CHANGED, refreshCount);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
      window.removeEventListener(CHANGED, refreshCount);
    };
  }, [refreshCount]);

  return { unread, latest, loading, loadLatest, markRead, markAllRead, refreshCount };
};

export default useAdminNotifications;
